import React, { useEffect, useState } from 'react';
import { Card, Grid, IconButton } from '@mui/material';
import HttpServices from 'src/services/httpService';
import { reportModuleURL } from 'src/services/urlService';
import { useToaster } from 'src/utils/toaster/toasterContext';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import NoReportCard from 'src/components/reports/summary-cards/noReportCard';
import ReportActionPopup from 'src/pages/reports/reportActionPopup';
import MapChart from '../charts/map-chart';
import ColumnChart from '../charts/column.charts';
import BarChart from '../charts/bar-chart';
import TableDonutChart from '../summary-cards/summaryTableDonutCard';
import LineChart from '../charts/line-chart';
import StackedBarChart from '../charts/stacked-bar-chart';
import MultiplePostCard from '../charts/multiple-post-card';

const Charts = ({
  connectedProfileId,
  startDate,
  endDate,
  columnCharts,
  viewBy,
  serviceName,
  reportId,
  isCustomReport = false,
  fetchCustomReports,
}) => {
  const [isLoading, setLoading] = useState(true);
  const [summaryData, setSummaryData] = useState([]);
  const [open, setOpen] = useState(false); // Ensure open state for popover
  const [anchorEl, setAnchorEl] = useState(null); // For managing popover anchor
  const { showToast } = useToaster();

  useEffect(() => {
    if (connectedProfileId) {
      getAudienceSummary();
    }
  }, [connectedProfileId, startDate, endDate, viewBy]);

  const handlePopoverOpen = (event) => {
    setAnchorEl(event.currentTarget);
    setOpen(true);
  };

  const handlePopoverClose = () => {
    setOpen(false);
    setAnchorEl(null);
  };

  const getSummaryApi = () => {
    switch (serviceName) {
      case 'facebook':
        return reportModuleURL.facebookAudienceSummary;
      case 'instagram':
        return reportModuleURL.instagramAudienceSummary;
      case 'youtube':
        return reportModuleURL.youtubeAudienceSummary;
      case 'linkedin':
        return reportModuleURL.linkedinAudienceSummary;
      default:
        return null;
    }
  };

  const getAudienceSummary = () => {
    const apiUrl = getSummaryApi();
    if (!apiUrl) return;

    setLoading(true);
    const body = {
      connected_profile_id: connectedProfileId,
      report_id: columnCharts.id,
      date_range: {
        from: startDate,
        to: endDate,
      },
      view_by: viewBy,
    };

    HttpServices.Post({
      url: apiUrl,
      body: body,
      successCallback,
      failureCallback,
    });
  };

  const failureCallback = (message) => {
    setLoading(false);
    showToast(message, 'e');
    setSummaryData([]);
  };

  const successCallback = (data) => {
    const responseData = Array.isArray(data[0]?.data) ? data[0]?.data : [];
    setSummaryData(responseData);
    setLoading(false);
  };

  const renderComponent = (data) => {
    const commonStyle = {
      border: '1px solid #e0e0e0',
      borderRadius: '8px',
      padding: '16px',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
      transition: 'all 0.2s ease-in-out',
      '&:hover': {
        boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
        transform: 'translateY(-2px)',
      },
    };
    switch (data.card_type) {
      case 'map-chart':
        return (
          <Grid item xs={12} key={data.id}>
            <Card sx={commonStyle}>
              <MapChart
                title={data?.title}
                series={data?.series}
                categories={data?.categories}
                details={data?.details}
              />
            </Card>
          </Grid>
        );
      case 'column-chart':
        return (
          <Grid item xs={12} key={data.id}>
            <Card sx={commonStyle}>
              <ColumnChart title={data?.title} series={data?.series} categories={data?.categories} />
            </Card>
          </Grid>
        );
      case 'bar-chart':
        return (
          <Grid item xs={12} key={data.id}>
            <Card sx={commonStyle}>
              <BarChart
                title={data?.title}
                series={data?.series}
                categories={data?.categories}
                details={data?.details}
              />
            </Card>
          </Grid>
        );
      case 'stacked-bar-chart':
        return (
          <Grid item xs={12} key={data.id}>
            <Card sx={commonStyle}>
              <StackedBarChart
                title={data?.title}
                series={data?.series}
                categories={data?.categories}
              />
            </Card>
          </Grid>
        );
      case 'line-chart':
        return (
          <Grid item xs={12} key={data.id}>
            <Card sx={commonStyle}>
              <LineChart
                title={data?.title}
                series={data?.series}
                categories={data?.categories}
                details={data?.details}
              />
            </Card>
          </Grid>
        );
      case 'multiple-post-card':
        return (
          <Grid item xs={12} key={data.id}>
            <Card sx={commonStyle}>
              <MultiplePostCard
                posts={data?.posts}
              />
            </Card>
          </Grid>
        );
      case 'table_with_donut':
        return (
          <Grid item xs={12} lg={6} key={data.id}>
            <Card sx={commonStyle}>
               <TableDonutChart
                  title={data?.title}
                  engagementPostType={data?.engagement_by_type}
                  value={data?.value}
                  labels={data?.key}
                />
            </Card>
          </Grid>
        );
      case 'no-report':
        return (
          <Grid item xs={12} key={data.id}>
            <Card sx={commonStyle}>
              <NoReportCard />
            </Card>
          </Grid>
        );
      default:
        return null;
    }
  };
  return (
    <Card
      sx={{
        position: 'relative',
        p: 3,
        borderRadius: '12px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)',
        border: '1px solid #e2e8f0',
      }}
    >
      {isLoading ? (
        <Grid container spacing={3} sx={{ minHeight: '300px', alignItems: 'stretch' }}>
          {Array.from({ length: 1 }).map((_, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Card
                sx={{
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  padding: '16px',
                  height: '100%',
                  backgroundColor: '#f8f9fa',
                }}
              >
                <NoReportCard />
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        <>
          {summaryData?.length > 0 ? (
            <Grid container spacing={3} sx={{ alignItems: 'stretch' }}>
              {summaryData?.map((data) => renderComponent(data))}
            </Grid>
          ) : (
            <Grid container spacing={3} sx={{ minHeight: '300px', alignItems: 'stretch' }}>
              {Array.from({ length: 1 }).map((_, index) => (
                <Grid item xs={12} sm={6} md={3} key={index}>
                  <Card
                    sx={{
                      border: '1px solid #e0e0e0',
                      borderRadius: '8px',
                      padding: '16px',
                      height: '100%',
                      backgroundColor: '#f8f9fa',
                    }}
                  >
                    <NoReportCard />
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </>
      )}
      <IconButton
        onClick={handlePopoverOpen}
        sx={{
          position: 'absolute',
          top: 16,
          right: 16,
          backgroundColor: 'rgba(255,255,255,0.9)',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          '&:hover': {
            backgroundColor: '#f5f5f5',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          },
          borderRadius: '50%',
          width: 40,
          height: 40,
        }}
      >
        <MoreHorizIcon sx={{ fontSize: 24, color: '#64748b' }} />
      </IconButton>
      <ReportActionPopup
        open={open}
        anchorEl={anchorEl}
        onClose={handlePopoverClose}
        serviceName={serviceName}
        startDate={startDate}
        endDate={endDate}
        viewBy={viewBy}
        connectedProfileId={connectedProfileId}
        reportId={reportId}
        currReportData={columnCharts}
        subReportId={columnCharts.id}
        isCustomReport={isCustomReport}
        fetchCustomReports={fetchCustomReports}
      />
    </Card>
  );
};
export default Charts;
